import React, { useMemo } from 'react';
import styles from './TipAnalysisBox.module.scss';

/* Providers */
import { useShopContext } from '../../../shared/__providers__/ShopContext';

/* Translations */
import { useTranslation } from 'react-i18next';
import { tr } from '../../../../../utils/translations/translator';

/* Interfaces */
import { IMediaFile, UnlockedTipData } from '../../../../../store/tips/reducer';
import { Credentials } from '../../../../../types/Login';
import { TipProduct } from '../../interfaces/TipProduct';

/* Redux */
import { RootState } from '../../../../../store';
import { connect } from 'react-redux';

/* Components */
import { ReactMarkdown } from 'react-markdown/lib/react-markdown';
import rehypeRaw from 'rehype-raw';
import rehypeSanitize, { defaultSchema } from 'rehype-sanitize';

const analysisSanitizeSchema = {
  ...defaultSchema,
  tagNames: [...(defaultSchema.tagNames ?? []), 'u', 'ins'],
};

interface StateProps {
  user: Credentials | null;
}

interface TipAnalysisBoxProps {}

export const TipAnalysisBox: React.FC<StateProps & TipAnalysisBoxProps> = ({
  user,
}) => {
  const { t } = useTranslation();
  const { shop } = useShopContext();
  const tip = shop!.selectedProduct as TipProduct;

  const videoImage = useMemo(() => {
    if (tip && tip.tip && tip.tip.media_files) {
      return tip.tip.media_files.find(
        (mediaFile: IMediaFile) => mediaFile.type === 'VIDEO',
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tip]);

  return (
    <>
      {tip && (
        <div className={styles.container}>
          <div className={styles.top}>
            <span className={styles.header}>{tr(t, 'tip_analysis')}</span>

            {videoImage && (
              <video className={styles.video} controls>
                <source src={videoImage.url} type="video/mp4" />
              </video>
            )}

            <span className={styles.analysis}>
              <ReactMarkdown
                rehypePlugins={[
                  rehypeRaw,
                  [rehypeSanitize, analysisSanitizeSchema],
                ]}
              >
                {(tip.tip as UnlockedTipData).analysis}
              </ReactMarkdown>
            </span>

            {tip.tip.media_files.map((mediaFile: IMediaFile) => {
              if (mediaFile.type === 'IMAGE') {
                return (
                  <img
                    key={mediaFile.id}
                    className={styles.mediaFileImage}
                    alt={mediaFile.name}
                    src={mediaFile.url}
                  />
                );
              }

              return null;
            })}
          </div>

          <div className={styles.bottom}>
            {!tip.tip.result && (
              <>
                <span className={styles.header}>
                  {tr(t, 'its_on_your_email')}{' '}
                  {user && user.user && user.user.username}!
                </span>

                <a
                  href={`tipsterchat://channel?tipster_id=${tip.tipster.ID}`}
                  className={styles.hrefButton}
                >
                  {tr(t, 'go-to-app')}
                </a>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
};

const mapStateToProps = (state: RootState) => ({
  user: state.login.user,
});

export const TipAnalysisBoxConnected = connect(mapStateToProps)(TipAnalysisBox);
